// Loads Google Identity Services (GIS, the "Sign in with Google" library) once per
// page and resolves with window.google.accounts.id.
//
// Same shape as shared/utils/calcomEmbed.js: a module-level promise, so concurrent
// callers share one <script>. One deliberate difference: a FAILED load forgets the
// promise, so a component that remounts after a network blip or a blocked request
// retries instead of being handed the cached rejection for the life of the page.
//
// This module never logs and never calls initialize(): the caller owns the client
// id and the credential callback (shared/components/GoogleSignInButton.jsx).

export const GSI_SCRIPT_URL = "https://accounts.google.com/gsi/client";
export const GSI_LOAD_TIMEOUT_MS = 10000;

let loadPromise = null;

// googleIdentityApi returns window.google.accounts.id once the GIS script has
// run, else null.
export function googleIdentityApi() {
  if (typeof window === "undefined") return null;
  return window.google?.accounts?.id || null;
}

// loadGoogleIdentity resolves with the GIS id API, injecting the script on the
// first call. It rejects with Error("gsi_script_failed"), Error("gsi_timeout") or
// Error("gsi_api_missing"); after a rejection the next call starts over.
export function loadGoogleIdentity() {
  const loaded = googleIdentityApi();
  if (loaded) return Promise.resolve(loaded);
  if (loadPromise) return loadPromise;

  const attempt = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    let settled = false;
    let timer = null;

    const finish = () => {
      settled = true;
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
    };
    const fail = (reason) => {
      if (settled) return;
      finish();
      script.remove();
      reject(new Error(reason));
    };
    const succeed = () => {
      if (settled) return;
      const api = googleIdentityApi();
      if (!api) {
        fail("gsi_api_missing");
        return;
      }
      finish();
      resolve(api);
    };

    script.src = GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = succeed;
    script.onerror = () => fail("gsi_script_failed");
    timer = setTimeout(() => fail("gsi_timeout"), GSI_LOAD_TIMEOUT_MS);
    document.head.appendChild(script);
  });

  // Forget a failed attempt so the next caller injects a fresh script. Attached
  // before the promise is handed out, so it runs before any caller's own handler.
  attempt.catch(() => {
    if (loadPromise === attempt) loadPromise = null;
  });

  loadPromise = attempt;
  return attempt;
}

// resetGoogleIdentityLoaderForTests forgets the in-flight or settled promise.
// Tests clear window.google and the injected <script> themselves.
export function resetGoogleIdentityLoaderForTests() {
  loadPromise = null;
}
